import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "utils/AuthContext";
import { FlexLeftCol, Container, Flex } from "styles/global-style";
import { getDefaultProjectData } from "utils/DefaultProjectData";
import arrowRight from "imgs/arrow-right.png";

export default function Profile() {
  const { personId } = useParams();
  const navigate = useNavigate();
  const { supabase, user, refresh, roleAccess } = useContext(AuthContext);
  const [person, setPerson] = useState(null);
  const [disciplines, setDisciplines] = useState([]);
  const [selectedDiscipline, setSelectedDiscipline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditingName, setIsEditingName] = useState(false);
  const [firstnameValue, setFirstnameValue] = useState("");
  const [lastnameValue, setLastnameValue] = useState("");
  const firstnameInputRef = useRef(null);
  const lastnameInputRef = useRef(null);

  // Load person data
  useEffect(() => {
    if (!supabase || !personId) return;

    const loadPerson = async () => {
      const { data, error } = await supabase
        .from("people")
        .select("*")
        .eq("id", personId)
        .maybeSingle();

      if (error) {
        console.error("Error loading person:", error);
        navigate("/auth/timeline");
        return;
      }

      if (!data) {
        navigate("/auth/timeline");
        return;
      }

      setPerson(data);
      // Set selected discipline from person data
      if (data.discipline_id) {
        setSelectedDiscipline(data.discipline_id);
      }
      // Initialize name values
      setFirstnameValue(data.firstname || "");
      setLastnameValue(data.lastname || "");
    };

    loadPerson();
  }, [supabase, personId, navigate]);

  // Load disciplines for the project
  useEffect(() => {
    if (!supabase || !user?.proj?.id || !personId) return;

    const loadDisciplines = async () => {
      const projectType = user.proj.type;
      if (!projectType) {
        setDisciplines([]);
        setLoading(false);
        return;
      }

      // Get default disciplines
      const defaultDisciplines = getDefaultProjectData(projectType).map(({ doDs, ...discipline }) => discipline);

      // Try to load custom disciplines from settings
      const { data: setting } = await supabase
        .from("settings")
        .select("value")
        .eq("project_id", user.proj.id)
        .eq("name", "project_disciplines")
        .maybeSingle();

      let availableDisciplines = defaultDisciplines;
      if (setting?.value) {
        try {
          const customDisciplines = JSON.parse(setting.value);
          const defaultMap = new Map(defaultDisciplines.map(d => [d.id, d]));
          const merged = customDisciplines.map(customDiscipline => {
            const defaultDiscipline = defaultMap.get(customDiscipline.id);
            return defaultDiscipline ? { ...defaultDiscipline, ...customDiscipline } : customDiscipline;
          });
          merged.sort((a, b) => (a.order || 0) - (b.order || 0));
          availableDisciplines = merged;
        } catch (e) {
          console.error("Failed to parse project_disciplines setting:", e);
        }
      }

      setDisciplines(availableDisciplines);
      setLoading(false);
    };

    loadDisciplines();
  }, [supabase, user, personId]);

  // Save discipline selection
  const handleDisciplineChange = useCallback(async (e) => {
    const disciplineId = e.target.value === "none" ? null : e.target.value;
    setSelectedDiscipline(disciplineId);

    if (!supabase || !personId) return;

    // Update person's discipline_id in people table
    await supabase
      .from("people")
      .update({ discipline_id: disciplineId })
      .eq("id", personId);

    // Update local person state
    setPerson(prev => prev ? { ...prev, discipline_id: disciplineId } : null);

    await refresh();
  }, [supabase, personId, refresh]);

  // Handle name editing
  const handleNameSave = useCallback(async () => {
    if (!supabase || !personId || !person) return;

    const trimmedFirstname = firstnameValue.trim();
    const trimmedLastname = lastnameValue.trim();

    // If both are empty, restore original name
    if (!trimmedFirstname && !trimmedLastname) {
      setFirstnameValue(person.firstname || "");
      setLastnameValue(person.lastname || "");
      setIsEditingName(false);
      return;
    }

    // Update person's name in people table
    const { error } = await supabase
      .from("people")
      .update({ 
        firstname: trimmedFirstname,
        lastname: trimmedLastname
      })
      .eq("id", personId);

    if (!error) {
      // Update local person state
      setPerson(prev => prev ? { 
        ...prev, 
        firstname: trimmedFirstname,
        lastname: trimmedLastname
      } : null);
      await refresh();
    }

    setIsEditingName(false);
  }, [supabase, personId, person, firstnameValue, lastnameValue, refresh]);

  // Focus firstname input when editing starts
  useEffect(() => {
    if (isEditingName && firstnameInputRef.current) {
      firstnameInputRef.current.focus();
      firstnameInputRef.current.select();
    }
  }, [isEditingName]);

  // Update name values when person changes (but not when editing)
  useEffect(() => {
    if (person && !isEditingName) {
      setFirstnameValue(person.firstname || "");
      setLastnameValue(person.lastname || "");
    }
  }, [person, isEditingName]);

  if (loading || !person) {
    return (
      <Container>
        <div>Loading...</div>
      </Container>
    );
  }

  const personName = `${person.firstname || ""} ${person.lastname || ""}`.trim() || "Unknown";

  // Prepare dropdown items (add "None" option)
  const disciplineOptions = [
    { id: "none", name: "None", color: null },
    ...disciplines.map(d => ({ id: d.id, name: d.name, color: d.color }))
  ];
  const disciplineDisplayValue = selectedDiscipline || "none";
  
  // Find the selected discipline to get its color
  const selectedDisciplineData = disciplines.find(d => d.id === selectedDiscipline);
  const disciplineColor = selectedDisciplineData?.color || null;

  return (
    <Container>
      <FlexLeftCol style={{ gap: "1.5rem" }}>
        <Flex style={{ alignItems: "center", gap: "0.75rem" }}>
          <div
            onClick={() => navigate("/auth/timeline")}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              padding: "4px",
              borderRadius: "4px",
              transition: "background-color 0.2s"
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.1)"}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
          >
            <img 
              alt="back" 
              src={arrowRight} 
              style={{ 
                width: 16, 
                height: 16, 
                filter: "invert(1)",
                transform: "rotate(180deg)"
              }} 
            />
          </div>
          {roleAccess?.("lead") && isEditingName ? (
            <Flex style={{ alignItems: "center", gap: "0.5rem", flex: 1, flexWrap: "wrap" }}>
              <input
                ref={firstnameInputRef}
                type="text"
                value={firstnameValue}
                onChange={(e) => setFirstnameValue(e.target.value)}
                onBlur={(e) => {
                  // Don't save if focus is moving to the lastname input
                  requestAnimationFrame(() => {
                    const activeElement = document.activeElement;
                    if (activeElement !== lastnameInputRef.current) {
                      handleNameSave();
                    }
                  });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleNameSave();
                  } else if (e.key === 'Escape') {
                    setFirstnameValue(person.firstname || "");
                    setLastnameValue(person.lastname || "");
                    setIsEditingName(false);
                  } else if (e.key === 'Tab' && !e.shiftKey && lastnameInputRef.current) {
                    e.preventDefault();
                    lastnameInputRef.current.focus();
                    lastnameInputRef.current.select();
                  }
                }}
                placeholder="First name"
                className="profile-name-input"
                style={{
                  margin: 0,
                  textAlign: "left",
                  fontSize: "80px",
                  fontFamily: '"Markazi Text", serif',
                  fontWeight: 600,
                  lineHeight: 1.35,
                  background: "transparent",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  borderRadius: "4px",
                  outline: "none",
                  padding: "4px 8px",
                  color: "inherit",
                  cursor: "text",
                  minWidth: "150px",
                  flex: 1,
                  transform: "translateX(-5px)"
                }}
              />
              <input
                ref={lastnameInputRef}
                type="text"
                value={lastnameValue}
                onChange={(e) => setLastnameValue(e.target.value)}
                onBlur={(e) => {
                  // Don't save if focus is moving to the firstname input
                  requestAnimationFrame(() => {
                    const activeElement = document.activeElement;
                    if (activeElement !== firstnameInputRef.current) {
                      handleNameSave();
                    }
                  });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleNameSave();
                  } else if (e.key === 'Escape') {
                    setFirstnameValue(person.firstname || "");
                    setLastnameValue(person.lastname || "");
                    setIsEditingName(false);
                  } else if (e.key === 'Tab' && e.shiftKey && firstnameInputRef.current) {
                    e.preventDefault();
                    firstnameInputRef.current.focus();
                    firstnameInputRef.current.select();
                  }
                }}
                placeholder="Last name"
                className="profile-name-input"
                style={{
                  margin: 0,
                  textAlign: "left",
                  fontSize: "80px",
                  fontFamily: '"Markazi Text", serif',
                  fontWeight: 600,
                  lineHeight: 1.35,
                  background: "transparent",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  borderRadius: "4px",
                  outline: "none",
                  padding: "4px 8px",
                  color: "inherit",
                  cursor: "text",
                  minWidth: "150px",
                  flex: 1
                }}
              />
            </Flex>
          ) : (
            <h1 
              style={{ 
                margin: 0, 
                textAlign: "left",
                cursor: roleAccess?.("lead") ? "pointer" : "default"
              }}
              onClick={() => {
                if (roleAccess?.("lead")) {
                  setIsEditingName(true);
                  // Set initial values when starting to edit
                  setFirstnameValue(person.firstname || "");
                  setLastnameValue(person.lastname || "");
                }
              }}
              onMouseEnter={(e) => {
                if (roleAccess?.("lead")) {
                  e.currentTarget.style.opacity = "0.8";
                }
              }}
              onMouseLeave={(e) => {
                if (roleAccess?.("lead")) {
                  e.currentTarget.style.opacity = "1";
                }
              }}
            >
              {personName}
            </h1>
          )}
        </Flex>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", maxWidth: "300px" }}>
          <label style={{ fontSize: "0.875rem", fontWeight: 500 }}>Discipline</label>
          <select
            className="select"
            style={{ 
              padding: "8px 12px",
              fontSize: "0.875rem",
              borderRadius: "4px",
              border: "none",
              outline: "none",
              backgroundColor: disciplineColor || "white",
              color: disciplineColor ? "white" : "black",
              cursor: "pointer",
              maxWidth: "100%"
            }}
            value={disciplineDisplayValue}
            onChange={handleDisciplineChange}
          >
            {disciplineOptions.map((option) => (
              <option 
                key={option.id} 
                value={option.id}
                style={{ 
                  backgroundColor: option.color || "white",
                  color: option.color ? "white" : "black"
                }}
              >
                {option.name}
              </option>
            ))}
          </select>
        </div>
      </FlexLeftCol>
    </Container>
  );
}

